import { ref, onUnmounted } from 'vue'
import { locationLog, locationWarn } from '../utils/locationDebug'

interface LocationState {
  latitude: number | null
  longitude: number | null
  accuracy: number | null
  timestamp: number | null
  isTracking: boolean
  hasPermission: boolean
  error: string | null
}

const locationState = ref<LocationState>({
  latitude: null,
  longitude: null,
  accuracy: null,
  timestamp: null,
  isTracking: false,
  hasPermission: false,
  error: null
})

let watchId: number | null = null

// localStorage Keys
const LOCATION_PERMISSION_KEY = 'ragacars_location_permission'
const LOCATION_PERMISSION_TIMESTAMP_KEY = 'ragacars_location_permission_timestamp'

// Speichere Standort-Einwilligung in localStorage
const saveLocationPermission = (granted: boolean) => {
  try {
    localStorage.setItem(LOCATION_PERMISSION_KEY, granted ? 'granted' : 'denied')
    localStorage.setItem(LOCATION_PERMISSION_TIMESTAMP_KEY, Date.now().toString())
  } catch (error) {
    console.error('Fehler beim Speichern der Standort-Einwilligung:', error)
  }
}

// Lade Standort-Einwilligung aus localStorage.
// Ab Paket E nur noch Cache, nicht mehr die Wahrheit — die liefert
// verifyLocationPermission() vom Gerät.
export const loadLocationPermission = (): boolean => {
  try {
    const permission = localStorage.getItem(LOCATION_PERMISSION_KEY)
    const timestamp = localStorage.getItem(LOCATION_PERMISSION_TIMESTAMP_KEY)
    
    if (permission === 'granted' && timestamp) {
      // Prüfe, ob die Einwilligung nicht älter als 30 Tage ist
      const permissionAge = Date.now() - parseInt(timestamp)
      const thirtyDaysInMs = 30 * 24 * 60 * 60 * 1000
      
      if (permissionAge < thirtyDaysInMs) {
        return true
      } else {
        // Einwilligung ist abgelaufen, entferne sie
        clearLocationPermission()
        return false
      }
    }
    
    return false
  } catch (error) {
    console.error('Fehler beim Laden der Standort-Einwilligung:', error)
    return false
  }
}

// Lösche Standort-Einwilligung aus localStorage
const clearLocationPermission = () => {
  try {
    localStorage.removeItem(LOCATION_PERMISSION_KEY)
    localStorage.removeItem(LOCATION_PERMISSION_TIMESTAMP_KEY)
  } catch (error) {
    console.error('Fehler beim Löschen der Standort-Einwilligung:', error)
  }
}

/**
 * Ergebnis einer echten Prüfung am Gerät.
 * 'unknown' heißt ausdrücklich: wir wissen es nicht (kein Signal, Timeout,
 * Tiefgarage). Das darf NIE zu einer Sperre führen.
 */
export type LocationPermissionVerdict = 'granted' | 'denied' | 'unknown'

// Permissions-API abfragen. Liefert null, wenn es sie nicht gibt (ältere
// iOS-Safari) oder sie für 'geolocation' wirft.
const queryPermissionState = async (): Promise<PermissionState | null> => {
  try {
    if (!navigator.permissions || typeof navigator.permissions.query !== 'function') {
      return null
    }
    const status = await navigator.permissions.query({ name: 'geolocation' as PermissionName })
    return status.state
  } catch (error) {
    locationWarn('Permissions-API für Geolocation nicht nutzbar:', error)
    return null
  }
}

// Rückfall: tatsächlich eine Position anfordern und am Fehlercode ablesen,
// ob der Nutzer abgelehnt hat (Code 1) oder es nur gerade nicht klappt (2/3).
const probeGeolocation = (timeoutMs = 8000): Promise<LocationPermissionVerdict> => {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve('unknown')
      return
    }

    let settled = false
    const finish = (verdict: LocationPermissionVerdict) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      resolve(verdict)
    }

    const timer = setTimeout(() => finish('unknown'), timeoutMs)

    navigator.geolocation.getCurrentPosition(
      () => finish('granted'),
      (error) => finish(error.code === 1 ? 'denied' : 'unknown'),
      {
        enableHighAccuracy: false,
        timeout: Math.max(1000, timeoutMs - 2000),
        maximumAge: 60000
      }
    )
  })
}

/**
 * Fragt das GERÄT, nicht den localStorage. Der gespeicherte Wert ist ab hier
 * nur noch Cache und wird bei jeder eindeutigen Antwort nachgeführt.
 */
export const verifyLocationPermission = async (): Promise<LocationPermissionVerdict> => {
  const state = await queryPermissionState()

  if (state === 'granted') {
    locationState.value.hasPermission = true
    saveLocationPermission(true)
    return 'granted'
  }

  if (state === 'denied') {
    locationState.value.hasPermission = false
    clearLocationPermission()
    return 'denied'
  }

  // 'prompt' oder keine Permissions-API → wirklich nachfragen
  const verdict = await probeGeolocation()

  if (verdict === 'granted') {
    locationState.value.hasPermission = true
    saveLocationPermission(true)
  } else if (verdict === 'denied') {
    locationState.value.hasPermission = false
    clearLocationPermission()
  }

  return verdict
}

/**
 * Die eine Frage, die das Berechtigungs-Gate stellen soll: aussperren oder nicht?
 * Nur eine ausdrückliche Ablehnung sperrt. Bei 'unknown' gilt der Cache weiter —
 * ein Fahrer in der Tiefgarage darf nicht aus der App fliegen.
 */
export const isLocationBlocked = async (): Promise<boolean> => {
  const verdict = await verifyLocationPermission()

  if (verdict === 'denied') return true
  if (verdict === 'granted') return false

  return !loadLocationPermission()
}

// Live-Überwachung: reagiert sofort, wenn die Ortung am Gerät entzogen oder
// wieder erteilt wird — statt es erst bei der nächsten Abfrage zu merken.
let permissionStatus: PermissionStatus | null = null
let permissionChangeHandler: (() => void) | null = null

export const stopWatchingLocationPermission = (): void => {
  if (permissionStatus && permissionChangeHandler) {
    permissionStatus.removeEventListener('change', permissionChangeHandler)
  }
  permissionStatus = null
  permissionChangeHandler = null
}

export const watchLocationPermission = async (
  onChange: (state: PermissionState) => void
): Promise<boolean> => {
  try {
    if (!navigator.permissions || typeof navigator.permissions.query !== 'function') {
      return false
    }

    const status = await navigator.permissions.query({ name: 'geolocation' as PermissionName })

    stopWatchingLocationPermission()

    permissionStatus = status
    permissionChangeHandler = () => {
      locationLog('Standort-Berechtigung am Gerät geändert:', status.state)

      if (status.state === 'granted') {
        saveLocationPermission(true)
      } else if (status.state === 'denied') {
        clearLocationPermission()
      }
      locationState.value.hasPermission = status.state === 'granted'

      onChange(status.state)
    }

    status.addEventListener('change', permissionChangeHandler)
    return true
  } catch (error) {
    locationWarn('Standort-Berechtigung kann nicht überwacht werden:', error)
    return false
  }
}

// Prüfe den aktuellen Standort-Status
export const checkLocationPermission = (): Promise<PermissionState> => {
  return new Promise((resolve) => {
    if (!navigator.permissions) {
      // Fallback für Browser ohne Permissions API
      resolve('prompt')
      return
    }
    
    navigator.permissions.query({ name: 'geolocation' as PermissionName })
      .then((result) => {
        resolve(result.state)
      })
      .catch(() => {
        resolve('prompt')
      })
  })
}

// Fordere Standortzugriff an
export const requestLocationPermission = async (): Promise<boolean> => {
  try {
    const position = await new Promise<GeolocationPosition>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000
      })
    })
    
    // Aktualisiere den Status
    locationState.value.hasPermission = true
    locationState.value.latitude = position.coords.latitude
    locationState.value.longitude = position.coords.longitude
    locationState.value.accuracy = position.coords.accuracy
    locationState.value.timestamp = position.timestamp
    locationState.value.error = null
    
    // Speichere die Einwilligung
    saveLocationPermission(true)
    
    locationLog('Standortzugriff gewährt:', position)
    return true
    
  } catch (error) {
    console.error('Standortzugriff verweigert oder Fehler:', error)
    locationState.value.hasPermission = false
    locationState.value.error = error instanceof Error ? error.message : 'Unbekannter Fehler'
    
    // Speichere die Verweigerung
    saveLocationPermission(false)
    
    return false
  }
}

// Starte dauerhaftes Tracking
export const startLocationTracking = async (): Promise<boolean> => {
  if (!navigator.geolocation) {
    locationState.value.error = 'Geolocation wird von diesem Browser nicht unterstützt'
    return false
  }
  
  try {
    // Stoppe vorheriges Tracking
    stopLocationTracking()
    
    // Starte neues Tracking
    watchId = navigator.geolocation.watchPosition(
      (position) => {
        locationState.value.latitude = position.coords.latitude
        locationState.value.longitude = position.coords.longitude
        locationState.value.accuracy = position.coords.accuracy
        locationState.value.timestamp = position.timestamp
        locationState.value.isTracking = true
        locationState.value.error = null
        
        locationLog('Standort aktualisiert:', {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy
        })
      },
      (error) => {
        locationState.value.isTracking = false
        locationState.value.error = error.message
        console.error('Standort-Tracking Fehler:', error)
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 30000 // Aktualisiere alle 30 Sekunden
      }
    )
    
    locationState.value.hasPermission = true
    return true
    
  } catch (error) {
    locationState.value.error = error instanceof Error ? error.message : 'Unbekannter Fehler'
    return false
  }
}

// Stoppe dauerhaftes Tracking
export const stopLocationTracking = () => {
  if (watchId !== null) {
    navigator.geolocation.clearWatch(watchId)
    watchId = null
    locationState.value.isTracking = false
    locationLog('Standort-Tracking gestoppt')
  }
}

// Hole aktuellen Standort einmalig
export const getCurrentLocation = async (): Promise<GeolocationPosition | null> => {
  if (!navigator.geolocation) {
    locationState.value.error = 'Geolocation wird von diesem Browser nicht unterstützt'
    return null
  }
  
  try {
    const position = await new Promise<GeolocationPosition>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000
      })
    })
    
    // Aktualisiere den Status
    locationState.value.latitude = position.coords.latitude
    locationState.value.longitude = position.coords.longitude
    locationState.value.accuracy = position.coords.accuracy
    locationState.value.timestamp = position.timestamp
    locationState.value.hasPermission = true
    locationState.value.error = null
    
    return position
    
  } catch (error) {
    locationState.value.error = error instanceof Error ? error.message : 'Unbekannter Fehler'
    return null
  }
}

// Berechne Distanz zwischen zwei Koordinaten (Haversine-Formel)
export const calculateDistance = (
  lat1: number, 
  lon1: number, 
  lat2: number, 
  lon2: number
): number => {
  const R = 6371 // Erdradius in km
  const dLat = (lat2 - lat1) * Math.PI / 180
  const dLon = (lon2 - lon1) * Math.PI / 180
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a))
  return R * c
}

// Composable für die Verwendung
export const useLocation = () => {
  // Cleanup beim Unmount
  onUnmounted(() => {
    stopLocationTracking()
  })
  
  return {
    locationState: locationState.value,
    checkLocationPermission,
    verifyLocationPermission,
    isLocationBlocked,
    watchLocationPermission,
    stopWatchingLocationPermission,
    requestLocationPermission,
    startLocationTracking,
    stopLocationTracking,
    getCurrentLocation,
    calculateDistance,
    loadLocationPermission,
    saveLocationPermission,
    clearLocationPermission
  }
} 